import {isAbortError} from "../api/executor";
import {AssignUserApiInterface} from "../api/interface/assignable-users";
import {PersistenceCorrectionApiInterface} from "../api/interface/persistence-correction";
import {SearchValuationsApiInterface} from "../api/interface/search-valuation";
import {TabCountsApiInterface} from "../api/interface/tab-counts";
import {FREE_TEXT_LIMITS, type RevaFilterTab} from "../api/payload-types/search-valuation";
import {generateDefaultQueryError} from "../util/errorUtil";
import {selectCredentials} from "./authSlice";
import type {RevaThunkAction} from "./revaHooks";
import {SNAPSHOT_SCOPES, selectSnapshotsInitialized} from "./searchSnapshotsSlice";
import {loadSnapshotsPageThunk} from "./searchSnapshotsThunk";
import {
    checkTableParamsEquals,
    selectFreeText,
    selectNeedsRefresh,
    selectTableParams,
    setFreeText,
    setLoading,
    setPageLoaded,
    setQueryError,
    setTabCounts,
    setTabCountsError,
    setTabCountsLoading,
    updateTableParams,
    type ValuationsTableParams,
} from "./searchValuationsSlice";
import type {RevaExtraArgs} from "./store";

// AbortController der jeweils letzten Such-Anfrage PRO Tab: eine neue Anfrage bricht die vorherige
// (noch laufende) desselben Tabs ab, damit eine überholte Antwort nie die aktuelle überschreibt
// (greift v.a. beim schnellen Tippen im Live-Freitextfilter). Der bestehende Staleness-Schutz
// (checkTableParamsEquals + Freitext-Recheck) bleibt als zweite Verteidigungslinie.
const searchAbortControllers = new Map<RevaFilterTab, AbortController>();

// AbortController der letzten Tab-Counts-Anfrage (badge-Zahlen aller Tabs); ein neuer Tick bricht den
// vorigen Count-Request ab.
let tabCountsAbortController: AbortController | null = null;

/**
 * Aktualisiert die Parameter der Bewertungstabelle eines Startseiten-Tabs (Seite, Filter, Sortierung)
 * und lädt die passende Seite nach.
 * @param tab der Startseiten-Tab, dessen Tabelle aktualisiert wird
 * @param tableParams aktualisierte Tabellenparameter
 */
export const updateValuationsTableParamsThunk =
    (tab: RevaFilterTab, tableParams: Partial<ValuationsTableParams>): RevaThunkAction<Promise<void>> =>
    async (dispatch) => {
        dispatch(updateTableParams({tab, params: tableParams}));
        await dispatch(loadSearchPageThunk(tab));
    };

/**
 * Lädt die aktuell konfigurierte Suchseite eines Startseiten-Tabs (inkl. des globalen Freitextfilters).
 * @param tab der Startseiten-Tab, dessen Seite geladen wird
 * @param forceRefresh erzwingt ein Neuladen der aktuellen Seite, auch wenn sich keine Parameter geändert haben
 */
export const loadSearchPageThunk =
    (tab: RevaFilterTab, forceRefresh?: boolean | null): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {env, serviceApi}: RevaExtraArgs) => {
        if (!forceRefresh && !selectNeedsRefresh(getState(), tab)) {
            if (env.isDevelopmentMode) {
                console.log(`Skipp valuation page loading, because needsRefresh=false ...`);
            }
            return;
        }
        dispatch(setLoading(tab));

        const tableParams = selectTableParams(getState(), tab);
        const freeText = selectFreeText(getState());

        if (env.isDevelopmentMode) {
            console.log(`Loading valuations search (${tab}) on page ${tableParams.pageIndex} ...`);
        }

        // Vorherige (evtl. noch laufende) Suche dieses Tabs abbrechen.
        searchAbortControllers.get(tab)?.abort();
        const controller = new AbortController();
        searchAbortControllers.set(tab, controller);

        const credentials = selectCredentials(getState());
        try {
            const response = await new SearchValuationsApiInterface(tableParams, {
                freeText,
                signal: controller.signal,
            }).execute(serviceApi, credentials!);
            if (env.isDevelopmentMode) {
                console.log(`Valuations Page ${tableParams.pageIndex} (${tab}) loaded:`, response);
            }
            // Prüfen, ob die Tabellenparameter UND der Freitext DIESES Tabs noch aktuell sind, oder ob
            // eine andere Ladeanfrage diese Antwort inzwischen überholt hat (der Freitext liegt global
            // außerhalb der tableParams, daher separat prüfen).
            const recheckTableParams = selectTableParams(getState(), tab);
            const recheckFreeText = selectFreeText(getState());
            if (checkTableParamsEquals(tableParams, recheckTableParams) && freeText === recheckFreeText) {
                dispatch(setPageLoaded({tab, result: response}));
            } else if (env.isDevelopmentMode) {
                console.log(
                    `Skip applying loaded table data response, because the table parameters have changed in the meantime`,
                );
            }
        } catch (err) {
            // Abgebrochene Anfrage (ein neuer Tastendruck hat sie überholt) still schlucken.
            if (isAbortError(err)) {
                return;
            }
            dispatch(setQueryError({tab, error: generateDefaultQueryError(err as Error)}));
        }
    };

/**
 * Lädt die Trefferzahlen aller sichtbaren Startseiten-Tabs für den aktuellen Freitextfilter (Badges).
 * Bricht eine noch laufende vorige Count-Anfrage ab.
 */
export const loadTabCountsThunk =
    (): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {serviceApi}: RevaExtraArgs) => {
        tabCountsAbortController?.abort();
        const controller = new AbortController();
        tabCountsAbortController = controller;

        dispatch(setTabCountsLoading());
        const freeText = selectFreeText(getState());
        const credentials = selectCredentials(getState());
        try {
            const result = await new TabCountsApiInterface(freeText, controller.signal).execute(
                serviceApi,
                credentials!,
            );
            // Nur anwenden, wenn dies noch die aktuellste Count-Anfrage ist.
            if (tabCountsAbortController === controller) {
                dispatch(setTabCounts(result));
            }
        } catch (err) {
            if (isAbortError(err)) {
                return;
            }
            dispatch(setTabCountsError());
        }
    };

/**
 * Entscheidet, ob eine Roheingabe der Startseiten-Suchzeile als Freitextfilter taugt (#19380), und liefert
 * den normalisierten Wert oder `null` für "kein Filter". Whitespace-Folgen (auch die Umbrüche eines
 * eingefügten Blocks) werden zu je einem Leerzeichen zusammengefasst, damit hier dieselbe Zeichen- und
 * Suchwortzahl gezählt wird wie serverseitig.
 *
 * Kein Filter ist es in vier Fällen: leer bzw. kürzer als die Mindestlänge (schränkt nie ein), länger als
 * {@link FREE_TEXT_LIMITS.maxLength}, mehr als {@link FREE_TEXT_LIMITS.maxTokens} Suchwörter, oder ein
 * eingefügtes JSON-Objekt. Die Suchzeile ist der Multi-Input: ein JSON-Block ist dort eine gültige Eingabe
 * für den Import per Enter (siehe AutoDetectMultiInput), nur eben kein Suchbegriff. Deshalb wird das Feld
 * bewusst NICHT in seiner Länge begrenzt — stattdessen unterbleibt die Filteranfrage, die die API ohnehin
 * mit HTTP 422 ablehnen würde.
 */
const effectiveFreeText = (text: string | null): string | null => {
    const normalized = (text ?? "").replace(/\s+/g, " ").trim();
    if (
        normalized.length < FREE_TEXT_LIMITS.minLength ||
        normalized.length > FREE_TEXT_LIMITS.maxLength ||
        normalized.split(" ").length > FREE_TEXT_LIMITS.maxTokens ||
        /^\{.*}$/s.test(normalized)
    ) {
        return null;
    }
    return normalized;
};

/**
 * Setzt den globalen Live-Freitextfilter aus der Startseiten-Suchzeile. Ab 3 Zeichen wird gefiltert; bei
 * leerem Feld und bei einer Eingabe, die kein Suchbegriff sein kann (siehe {@link effectiveFreeText}), wird
 * der Filter zurückgesetzt. Bei einer echten Änderung: alle Tab-States als aktualisierungsbedürftig
 * markieren (der aktuell gemountete Tab lädt sich über seinen eigenen Effekt sofort neu, siehe
 * ValuationsTable), bereits initialisierte Snapshot-Scopes (DONE/Admin-Historie) neu laden und die
 * Tab-Count-Badges neu berechnen.
 * @param text Roheingabe aus der Suchzeile (oder null zum Zurücksetzen)
 */
export const setFreeTextThunk =
    (text: string | null): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState) => {
        const effective = effectiveFreeText(text);
        if (selectFreeText(getState()) === effective) {
            return;
        }
        dispatch(setFreeText(effective));

        const tasks: Promise<unknown>[] = [dispatch(loadTabCountsThunk())];
        // Bereits geladene Snapshot-Listen (eigene Historie / Admin-Historie) mit dem neuen Freitext
        // neu laden — auch wenn ihr Tab gerade nicht gemountet ist (die Snapshot-Slice kennt kein
        // needsRefresh); nicht-initialisierte Scopes ziehen den Freitext beim ersten Laden mit.
        for (const scope of SNAPSHOT_SCOPES) {
            if (selectSnapshotsInitialized(getState(), scope)) {
                // Zurück auf Seite 0: die alte Seiten-Position gehört zur alten Treffermenge.
                tasks.push(dispatch(loadSnapshotsPageThunk(scope, 0)));
            }
        }
        await Promise.all(tasks);
    };

/**
 * Weist den Owner einer Bewertung zu (username=null gibt sie zurück in den Pool) — bewegt sie damit
 * zwischen "Meine Bewertungen" und "Auftragspool". Ruft den Assign-Endpunkt direkt auf statt über den
 * `assignValuation`-Thunk aus persistenceThunk, da dieser für die aktuell geladene Bewertung gedacht
 * ist und die Antwort fälschlich in die Persistence-Slice schreiben würde, die auf der Startseite gar
 * nicht angezeigt wird.
 * @param revaId die (neu) zuzuweisende Bewertung
 * @param username der neue Owner, oder null zum Zurückgeben in den Pool
 */
export const assignValuationOwnerThunk =
    (revaId: string, username: string | null): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {serviceApi}: RevaExtraArgs) => {
        const credentials = selectCredentials(getState());
        await new AssignUserApiInterface(revaId, username).execute(serviceApi, credentials!);
    };

/**
 * Öffnet eine finalisierte Bewertung wieder für eine Korrektur (lifeCycleState Final → Correction) und
 * gibt dabei automatisch ihren Owner frei (siehe reopenValuationForCorrection im Backend), sodass sie im
 * Auftragspool landet. Ruft denselben Endpunkt wie der `reopenPersistence`-Thunk aus persistenceThunk
 * auf, aber direkt und ohne dessen Seiteneffekte (Persistence-Slice, Objektparameter der aktuell
 * geladenen Bewertung) — die hier passende Bewertung ist irgendeine Zeile im Korrekturen-Tab, nicht
 * zwingend die gerade geöffnete. Der Pflicht-Kommentar (Grund der Korrektur) wird aus dem Bestätigungs-
 * Dialog des Korrekturen-Tabs übergeben; Vorab-Finalisierungsparameter gibt es hier nicht.
 * @param revaId die wieder zu öffnende Bewertung
 * @param comment Grund der Korrektur (Pflichtfeld aus dem Dialog)
 */
export const reopenForCorrectionThunk =
    (revaId: string, comment: string): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {serviceApi}: RevaExtraArgs) => {
        const credentials = selectCredentials(getState());
        await new PersistenceCorrectionApiInterface(revaId, comment, null).execute(serviceApi, credentials!);
    };
